import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { dateTime12 } from 'constants/dateFormat';
import moment from 'moment-timezone';
import { NSC_PATIENT, NSC_STUDY, NSPP_CARD, NSPP_PATIENT } from 'services/i18n/namespaces';

import { generateUrlByKey } from 'components/root/router';

import {
  cardAssignmentStatusMap,
  PAYMENT_TYPE_DIGITAL,
  PAYMENT_TYPE_PHYSICAL,
  paymentTypeMap,
  PHYSICAL_NOT_ASSIGNED
} from './constants';
import {
  patientsCardTypeExtendedFilterOperators,
  patientsStatusExtendedFilterOperators
} from './PatientsListCustomFilterOperators';

export default function useDataGrid() {
  const { t } = useTranslation();
  const [gridData, setGridData] = useState([]);

  const columns = useMemo(
    () => [
      {
        field: 'patientName',
        headerName: t('patient', { ns: NSPP_PATIENT }),
        flex: 1,
        renderCell({ row: { patientUniqueIdentifier, uniqueIdentifier, lastName, firstName, preferredName } }) {
          return (
            <Link
              to={generateUrlByKey('Patients.Patient Profile.Patient Studies', {
                patientId: patientUniqueIdentifier,
                ssuPatientId: uniqueIdentifier
              })}
              title="Go to Patient Info"
            >
              {`${lastName}, ${firstName}`}
              {preferredName && <span className="patient-preferred-name"> ({preferredName})</span>}
            </Link>
          );
        }
      },
      {
        field: 'subjectId',
        headerName: t('subjectId', { ns: NSC_PATIENT }),
        flex: 1,
        valueGetter(value) {
          return value || 'N/A';
        },
        minWidth: 180
      },
      {
        field: 'studyName',
        headerName: t('study'),
        flex: 1
      },
      {
        field: 'siteName',
        headerName: t('site'),
        flex: 1
      },
      {
        field: 'statusCode',
        headerName: t('status'),
        flex: 1,
        valueGetter(value) {
          return t(`statuses.${value}`, { ns: NSPP_PATIENT });
        },
        filterOperators: patientsStatusExtendedFilterOperators
      },
      {
        field: 'cardType',
        headerName: t('cardType', { ns: NSPP_PATIENT }),
        flex: 1,
        valueGetter(value, { cardType }) {
          return t(`statuses.${resolveCardType(cardType)}`, { ns: NSPP_CARD });
        },
        filterOperators: patientsCardTypeExtendedFilterOperators
      },
      {
        field: 'pcn',
        headerName: t('pcn', { ns: NSC_STUDY }),
        flex: 1
      },
      {
        field: 'patientId',
        headerName: t('patientId', { ns: NSPP_PATIENT }),
        flex: 1
      },
      {
        field: 'createdAt',
        headerName: t('creationDate'),
        flex: 1,
        valueGetter(value, { createdAt }) {
          return createdAt ? new Date(createdAt) : '';
        },
        renderCell({ row: { createdAt } }) {
          return createdAt ? moment(createdAt).format(dateTime12) : '';
        },
        type: 'date'
      }
    ],
    [t]
  );

  return [columns, gridData, setGridData];
}

function resolveCardType(cardType) {
  if (PAYMENT_TYPE_PHYSICAL === cardType) {
    return paymentTypeMap[PAYMENT_TYPE_PHYSICAL];
  }
  if (PAYMENT_TYPE_DIGITAL === cardType) {
    return paymentTypeMap[PAYMENT_TYPE_DIGITAL];
  }
  if (PHYSICAL_NOT_ASSIGNED === cardType) {
    return cardAssignmentStatusMap[PHYSICAL_NOT_ASSIGNED];
  }
  return 'N/A';
}
